import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTabsModule } from "@angular/material/tabs";
import { NgxGaugeModule } from "ngx-gauge";
import { DashboardMeasureDetailProd } from "@models/dashboard-measure-details.model";
import { DashboardMeasureDecile } from "@models/dashboard-measure-deciles.model";
import { MatChipsModule } from "@angular/material/chips";
import { MeasureCalculationDetailsProdComponent } from "./measure-calculation-details";
import { MipsCalculationService } from "@services/mips-calculation.service";

@Component({
  selector: "app-measure-details-prod",
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    NgxGaugeModule,
    MatChipsModule,
    MeasureCalculationDetailsProdComponent,
  ],
  templateUrl: "./measure-details.component.html",
  styleUrls: ["./measure-details.component.css"],
})
export class MeasureDetailsProdComponent implements OnChanges {
  @Input() measure!: DashboardMeasureDetailProd;
  @Input() relatedMeasures: DashboardMeasureDetailProd[] = [];
  @Input() showBenchmarks: boolean = true;
  @Input() isSmallPractice: boolean = false;
  @Input() deciles: DashboardMeasureDecile[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() viewScoringMeasure = new EventEmitter<DashboardMeasureDetailProd>();

  private readonly mipsCalculationService = inject(MipsCalculationService);

  isDragging = false;
  temporaryPerformance: number = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['measure']) {
      this.temporaryPerformance = this.measure.performance ?? 0;
    }
  }

  // True for an ordinary measure, the multi-performance parent, or the one child matching
  // performanceType. False for other siblings, whose score is always 0.
  get isDesignated(): boolean {
    return this.mipsCalculationService.isDesignatedChild(this.measure);
  }

  get points(): number {
    return this.measure.calculatedPoints ?? 0;
  }

  get scoringMeasure(): DashboardMeasureDetailProd | undefined {
    return this.mipsCalculationService.getScoringMeasure(this.measure, this.relatedMeasures);
  }

  get scoringMeasureLabel(): string {
    const target = this.scoringMeasure;
    if (!target || target.isMultiPerformanceParent) return 'the combined measure';
    return `Population ${target.populationNbr}`;
  }

  goToScoringMeasure(): void {
    const target = this.scoringMeasure;
    if (target) this.viewScoringMeasure.emit(target);
  }

  getPoints(newValue: number): number {
    if (!this.measure.isMIPS && !this.measure.isMVP) {
      return 0;
    }
    if (!this.isDesignated) {
      return 0;
    }
    // When displaying actual performance, use the pre-computed value from the store.
    // This ensures cross-population caseMinimumType lookups are reflected correctly
    // without needing to re-resolve siblings here.
    if (this.measure.performance != null && newValue === this.measure.performance && this.measure.calculatedPoints !== undefined) {
      return this.measure.calculatedPoints;
    }
    const caseMinSibling = this.mipsCalculationService.resolveCaseMinSibling(this.measure, this.relatedMeasures);
    return this.mipsCalculationService.getAchievementPointsForMeasure(
      this.measure,
      newValue,
      this.isSmallPractice,
      this.deciles,
      { completeness: caseMinSibling.completeness, caseMinimumMet: caseMinSibling.caseMinimumMet }
    );
  }

  closeDetails(): void {
    this.close.emit();
  }

  getFloorText(floor: number): string {
    if (floor == 5) {
      return "Subject to 5-point scoring floor if data completeness is met.";
    }
    if (floor == 7) {
      return "Subject to 7-point scoring floor if data completeness is met.";
    }
    return "";
  }

  isCompletenessMet(measure: DashboardMeasureDetailProd): boolean {
    return (measure.completeness ?? 0) >= 0.75;
  }

  getDataQualityMessage(measure: DashboardMeasureDetailProd): string {
    const caseMinMet = measure.effectiveCaseMinimumMet ?? false;
    const completenessNotMet = !this.isCompletenessMet(measure);

    if (!caseMinMet && completenessNotMet) {
      return "You need at least 20 eligible patients and data completeness to be met.";
    } else if (!caseMinMet) {
      return "You need at least 20 eligible patients to meet the case minimum.";
    } else if (completenessNotMet) {
      return "Data completeness requirement is not met.";
    }

    return "";
  }

  getFormatedValue(value: number): number {
    var percentage = value * 100;
    var rounded = Math.round(percentage * 100) / 100;
    return rounded;
  }

  getFormattedPoints(value: number): number {
    var rounded = Math.round(value * 100) / 100;
    return rounded;
  }

  startDragging(event: MouseEvent): void {
    this.isDragging = true;

    const sliderTrack = (event.target as HTMLElement).closest(
      ".slider-track"
    ) as HTMLElement;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!this.isDragging) return;

      const rect = sliderTrack.getBoundingClientRect();
      const offsetX = moveEvent.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, offsetX / rect.width));

      this.temporaryPerformance = percentage;
    };

    const onMouseUp = () => {
      this.isDragging = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }
}
